<template>
  <div v-if="canViewBetaTest">
    <Header title="ModCord Premium"></Header>

    <section class="info-page-loader" v-if="pageIsLoading">
      <div class="page-pulse">
        <div></div>
        <div></div>
        <div></div>
      </div>
    </section>

    <section v-if="!pageIsLoading">
      <div class="premium-introduction">
        <h1>More Safety Is Always Better.</h1>
        <p>A safer space offers a better experience for your moderators and for your members.</p>
      </div>

      <div class="premium-plan-section">
        <div id="plans" style="padding-bottom: 1.2em;" ref="plans"></div>
        <ul class="plan-list">
          <li class="plan-card" v-for="plan in subscriptionPlans" v-bind:key="plan.price_id">
            <div class="header" v-bind:style="`background-color: ${plan.theme_color};`">
              <div class="card-title">
                <h2>{{ plan.name }}</h2>
                <p v-bind:style="`color: ${plan.accent_color};`">{{ plan.description }}</p>
              </div>
            </div>

            <div class="body">
              <div class="price">
                <h2><span class="small">{{ currencyToSymbol(plan.currency) }}</span>{{ plan.price_amount }}<span class="small">/mo</span></h2>
                <p>Per Server</p>
              </div>
              <div class="perks">
                <ul class="perks-list">
                  <li v-for="perk in plan.perks" v-bind:key="perk">
                    <i class="fas fa-check-circle" v-bind:style="`color: ${plan.theme_color};`"></i>
                    <span>{{ perk }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <div class="footer">
              <button class="action-button" v-if="!plan.show_payment_methods" @click="() => showPaymentMethods(plan.price_id)" v-bind:style="`background-color: ${plan.theme_color}`">
                <p>Subscribe</p>
                <i class="fas fa-arrow-right"></i>
              </button>

              <button v-bind:class="{ 'action-button': true, 'buffering': true }" v-bind:style="`background-color: ${plan.theme_color}`" @click="() => checkoutStripeSubscription(plan.price_id)" v-if="plan.show_payment_methods">
                <i class="fas fa-credit-card"></i> <p>Debit or Credit Card</p>
              </button>

              <div class="paypal-button" v-bind:id="plan.price_id"></div>

              <p class="error-message" v-if="plan.error_message">{{ plan.error_message }}</p>
              <p class="success-message" v-if="plan.success_message">{{ plan.success_message }}</p>
            </div>
          </li>
        </ul>
      </div>

      <div class="table-section">
        <h1 class="table-title">Compare Plans</h1>
        <p class="table-description">Find what's best for your needs.</p>
        <table class="object-shadow">
          <tr>
            <th>Perk</th>
            <th v-for="plan in subscriptionPlans" v-bind:key="plan.price_id"><span>{{ plan.name }}</span></th>
          </tr>
          <tr>
            <td>Nsfw image filter</td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
          </tr>
          <tr>
            <td>Custom raid mode messages</td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
          </tr>
          <tr>
            <td>Auto disable raid mode</td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
          </tr>
          <tr>
            <td>Custom raid detection threshold</td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
          </tr>
          <tr>
            <td>Spam impersonation filter</td>
            <td><i class="fas fa-times-circle red-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
          </tr>
          <tr>
            <td>Schedule automated lockdowns</td>
            <td><i class="fas fa-times-circle red-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
          </tr>
          <tr>
            <td>Spam filter sensitivity</td>
            <td><i class="fas fa-times-circle red-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
          </tr>
          <tr>
            <td>Toggle punishment notifications</td>
            <td><i class="fas fa-times-circle red-icon"></i></td>
            <td><i class="fas fa-times-circle red-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
          </tr>
          <tr>
            <td>Set password to modify settings</td>
            <td><i class="fas fa-times-circle red-icon"></i></td>
            <td><i class="fas fa-times-circle red-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
          </tr>
          <tr>
            <td>Custom spoiler filter threshold</td>
            <td><i class="fas fa-times-circle red-icon"></i></td>
            <td><i class="fas fa-times-circle red-icon"></i></td>
            <td><i class="fas fa-check-circle green-icon"></i></td>
          </tr>
          <tr>
            <td>Automated punishments</td>
            <td>15</td>
            <td>10</td>
            <td><i class="fas fa-infinity"></i></td>
          </tr>
          <tr>
            <td>Custom commands</td>
            <td>30</td>
            <td>25</td>
            <td><i class="fas fa-infinity"></i></td>
          </tr>
        </table>
        <button class="action-button buffering redirect-button object-shadow" @click="scrollToPlans"><i class="fas fa-arrow-up"></i> <p>See plans</p></button>
      </div>

      <div class="page-overlay" v-if="showOverlay">
        <img v-bind:src="`${$cdnUrl}/assets/stripe-logo.png`" alt="Stripe Logo" class="overlay-banner">
        <h1 class="overlay-title"><i class="fas fa-lock"></i> Secure Checkout</h1>
        <p class="overlay-description">We never get to see your data. Our partner, stripe, securely collects, stores payment information and proccesses the payments, <a href="https://stripe.com/about" target="_blank" class="link">learn more</a>.</p>
        <p class="overlay-hint">Use the pop up window to continue.</p>
      </div>
    </section>
  </div>
</template>

<script>
import Header from "@/components/Header.vue";
import ResponseCodes from "@/utils/ResponseCodes.js";
import { mapGetters } from "vuex";
import { paypalClientId } from "../../conf.json";

export default {
  name: "Premium",
  components: { Header },
  watch: {
    login_queried () {
      if (this.login_queried && !this.canViewBetaTest) return window.location.href = "/page-not-found";
      this.loadSubscriptionPlans();
      // Should be moved to mount when it's out of beta.
    }
  },
  computed: {
    canViewBetaTest () {
      return this.beta_features.includes("PREMIUM_SUBSCRIPTIONS");
    },
    ...mapGetters([
      "id",
      "username",
      "authenticated",
      "login_queried",
      "beta_features"
    ]),
    canCreateSubscription () {
      return this.id && this.authenticated && this.verified_email;
    }
  },
  methods: {
    async loadSubscriptionPlans () {
      this.setLoadingState(true);
      let responsePayload = null;
      let mutatedPlanList = false;

      while (!responsePayload || responsePayload.status_code !== ResponseCodes.SUCCESS || !mutatedPlanList) {
        responsePayload = await this.$rest.api.subscriptions.get({
          use_authorization: false
        }).then((response) => response.payload).catch(() => {});
        if (responsePayload) mutatedPlanList = this.setPlanList(responsePayload.plans);
      }

      this.setLoadingState(false);
      setTimeout((self, win) => {
        if (win.location && win.location.hash === "#plans") self.scrollToPlans();
      }, 250, this, window);
    },
    setLoadingState (value) {
      this.pageIsLoading = value;
      return true;
    },
    markAsBuffering (price_id, value) {
      const planIndex = this.subscriptionPlans.findIndex(plan => plan.price_id === price_id);
      if (this.subscriptionPlans) this.subscriptionPlans[planIndex].buffering = value;
    },
    markOverlayAsShown (value) {
      document.body.classList.toggle("hide-overflow", value);
      this.showOverlay = value;
    },
    findPaypalPlanId (price_id) {
      const planIndex = this.subscriptionPlans.findIndex(plan => plan.price_id === price_id);
      return this.subscriptionPlans[planIndex].paypal_plan_id;
    },
    showPaymentMethods (price_id) {
      this.markPaymentMethodsAsShowing(price_id, true);

      const closure = {
        price_id,
        plan_id: this.findPaypalPlanId(price_id),
        rest: this.$rest,
        triggerFailedPayment: this.triggerFailedPayment,
        triggerSuccessPayment: this.triggerSuccessPayment,
        submitPayment: this.createCheckoutSession
      };
      paypal.Buttons({ // eslint-disable-line
        style: {
          height: 45,
          color: "blue"
        },
        createSubscription: (meta, action) => {
          return action.subscription.create({ plan_id: closure.plan_id });
        },
        onApprove: async (subscription) => {
          await closure.submitPayment(null, "paypal", subscription);
          closure.triggerSuccessPayment(closure.price_id);
        },
        onCancel: () => {
          closure.triggerFailedPayment(closure.price_id);
        }
      }).render(`#${price_id}`)
    },
    setPlanList (value) {
      if (!Array.isArray(value)) return false;
      this.subscriptionPlans = value.sort((a, b) => a.price_amount - b.price_amount).map(initialObject => ({
        buffering: "",
        error_message: "",
        success_message: "",
        show_payment_methods: "",
        ...initialObject
      }));
      return true;
    },
    setErrorMessage (price_id, value) {
      const planIndex = this.subscriptionPlans.findIndex(plan => plan.price_id === price_id);
      if (this.subscriptionPlans && planIndex > -1) this.subscriptionPlans[planIndex].error_message = value;
    },
    setSuccessMessage (price_id, value) {
      const planIndex = this.subscriptionPlans.findIndex(plan => plan.price_id === price_id);
      if (this.subscriptionPlans && planIndex > -1) this.subscriptionPlans[planIndex].success_message = value;
    },
    markPaymentMethodsAsShowing (price_id, value) {
      const planIndex = this.subscriptionPlans.findIndex(plan => plan.price_id === price_id);
      if (this.subscriptionPlans && planIndex > -1) this.subscriptionPlans[planIndex].show_payment_methods = value;
    },
    currencyToSymbol (currency) {
      if (currency === "usd") return "$";
      return "#";
    },
    async checkoutStripeSubscription (price_id) {
      this.markAsBuffering(price_id, true);
      this.setErrorMessage(price_id, "");
      this.setSuccessMessage(price_id, "");
      await this.launchStripeSubscriptionWindow(price_id).catch(() => {});
    },
    async launchStripeSubscriptionWindow (price_id) {
      const checkoutResponse = await this
        .createCheckoutSession(price_id, "stripe")
        .catch(() => {});

      if (!checkoutResponse) return this.setErrorMessage(price_id, "Couldn't get a checkout session.");
      else if (checkoutResponse.status === 500) return this.setErrorMessage(price_id, "Internal server error.");
      else if (checkoutResponse.status === 400) return this.setErrorMessage(price_id, "Invalid session request.");
      else if (checkoutResponse.status === 401) return this.setErrorMessage(price_id, "You must be logged in to subscribe.");
      else if (checkoutResponse.status === 403) return this.setErrorMessage(price_id, "You need a verified email address on your discord account.");

      try {
        const checkoutWindowWidth = 1100;
        const checkoutWindowHeight = 600;

        const dualScreenLeft = window.screenLeft !==  undefined ? window.screenLeft : window.screenX;
        const dualScreenTop = window.screenTop !==  undefined   ? window.screenTop  : window.screenY;

        const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
        const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;

        const systemZoom = width / window.screen.availWidth;
        const left = (width - checkoutWindowWidth) / 2 / systemZoom + dualScreenLeft;
        const top = (height - checkoutWindowHeight) / 2 / systemZoom + dualScreenTop;

        this.markOverlayAsShown(true);

        const checkoutWindow = window.open("", "_blank", 
          `
          scrollbars=yes,
          width=${checkoutWindowWidth / systemZoom}, 
          height=${checkoutWindowHeight / systemZoom}, 
          top=${top}, 
          left=${left}
          `
        );
        checkoutWindow.location.href = checkoutResponse.payload.checkout_url;

        if (window.focus) checkoutWindow.focus();

        let windowWatchInterval = setInterval(() => {
          try {
            if (!checkoutWindow.closed) {
              if (checkoutWindow.location.href.includes("/payments/success")) {
                checkoutWindow.close();
                this.markAsBuffering(price_id, false);
                this.markOverlayAsShown(false);
                clearInterval(windowWatchInterval);
                return this.triggerSuccessPayment(price_id);
              } else if (checkoutWindow.location.href.includes("payments/failure")) {
                checkoutWindow.close();
                clearInterval(windowWatchInterval);
                this.markAsBuffering(price_id, false);
                this.markOverlayAsShown(false)
                return this.triggerFailedPayment(price_id);
              }
            } else {
              this.markAsBuffering(price_id, false);
              this.markOverlayAsShown(false);
              clearInterval(windowWatchInterval);
              return this.setErrorMessage(price_id, "The checkout window was closed.");
            }
          } catch (error) {
            return;
          }
        }, 100);
      } catch (error) {
        this.markAsBuffering(price_id, false);
      }
    },
    async createCheckoutSession (price_id, gateway, subscription = {}) {
      let response = null;
      let retries = 0;

      while (!response || !response.payload || !response.status) {
        response = await this.$rest.api.subscriptions
          .post({
            data: {
              subscription,
              gateway,
              price_id
            }
          })
          .catch(() => {});
        retries++;
        if (retries > 2) break;
      }

      return response;
    },
    triggerSuccessPayment (price_id) {
      return this.setSuccessMessage(price_id, "The payment is being processed.");
    },
    triggerFailedPayment (price_id) {
      return this.setErrorMessage(price_id, "The payment was canceled.");
    },
    scrollToPlans () {
      const viewAncorElement = this.$refs.plans;
      viewAncorElement.scrollIntoView({ behavior:  "smooth" });
    }
  },
  data () {
    return {
      subscriptionPlans: [],
      pageIsLoading: true,
      showOverlay: false,
      disabledFundingSources: [
        "card",
        "bancontact"
      ]
    };
  },
  created () {
    const paypalSdkScript = document.createElement("script");

    let sdkUrl = "https://www.paypal.com/sdk/js";
    sdkUrl += `?client-id=${encodeURIComponent(paypalClientId)}`;
    sdkUrl += "&components=buttons";
    sdkUrl += `&disable-funding=${this.disabledFundingSources.join(",")}`;
    sdkUrl += `&intent=subscription`;
    sdkUrl += "&vault=true";

    paypalSdkScript.setAttribute("src", sdkUrl);
    document.head.appendChild(paypalSdkScript);
  }
}
</script>

<style>
  .premium-plan-section {
    width: 100%;
    padding: 2em 1em;
  }

  .premium-plan-section .plan-list {
    display: flex;
    flex-wrap: wrap;
    align-content: center;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  .premium-plan-section .plan-list .plan-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: var(--light-grey-blue);
    word-break: keep-all;
    margin: 1em;
    width: 30%;
    border-radius: 5px;
    overflow: hidden;
    max-width: 20.15em;
    min-width: 19em;
    -webkit-box-shadow: 0 4px 8px 0 rgba(0,0,0,0.2), 0 6px 20px 0 rgba(0,0,0,0.19);
    -moz-box-shadow: 0 4px 8px 0 rgba(0,0,0,0.2), 0 6px 20px 0 rgba(0,0,0,0.19);
    box-shadow: 0 4px 8px 0 rgba(0,0,0,0.2), 0 6px 20px 0 rgba(0,0,0,0.19);
  }

  .premium-plan-section .plan-list .plan-card .header {
    width: 100%;
  }

  .premium-plan-section .plan-list .plan-card .header .card-title {
    padding: 2em;
  }

  .premium-plan-section .plan-list .plan-card .body {
    padding: 2em;
    width: 100%;
  }

  .premium-plan-section .plan-list .plan-card .body .price h2 {
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2em;
    gap: 0.05em;
  }

  .premium-plan-section .plan-list .plan-card .body .price h2 .small {
    font-size: 0.55em;
    font-weight: 500;
  }

  .premium-plan-section .plan-list .plan-card .body .price p {
    text-align: center;
    font-variant: all-small-caps;
    font-size: 1.1em;
    position: relative;
    top: -0.5em;
  }

  .premium-plan-section .plan-list .plan-card .body .perks {
    margin-top: 0.9em;
  }

  .premium-plan-section .plan-list .plan-card .body .perks .perks-list {
    display: flex;
    flex-direction: column;
    gap: .45em;
    font-weight: 300;
    font-size: 0.8em;
  }

  .premium-plan-section .plan-list .plan-card .body .perks .perks-list li i {
    position: relative;
    top: 0.15em;
    font-size: 1.7em;
  }

  .premium-plan-section .plan-list .plan-card .body .perks .perks-list li span {
    margin-left: 0.5em;
  }
 
  .premium-plan-section .plan-list .plan-card .footer {
    padding: 2em;
    width: 100%;
    background-color: var(--faint-dark);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5em;
  }

  .action-button {
    width: 100%;
    padding: 0.624em;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid rgba(0, 0, 0, 0);
    border-radius: 3px;
    color: var(--white);
    font-size: 1em;
    cursor: pointer;
    transition: all .2s;
    margin-top: 0.5em;
  }

  .action-button img {
    height: 29px;
    position: relative;
    left: -0.2em;
  }

  .premium-plan-section .plan-list .plan-card .footer .paypal-button {
    width: 100%;
  }

  .action-button.buffering {
    align-items: center;
    justify-content: center;
    gap: 0.4em;
  }

  .action-button:hover {
    filter: brightness(80%);
  }

  .error-message {
    font-size: 0.8em;
    color: var(--red);
  }

  .success-message {
    font-size: 0.8em;
    color: var(--green);
  }

  .hide-overflow {
    overflow: hidden;
  }

  .page-overlay {
    height: 100vh;
    width: 100vw;
    top: 0;
    left: 0;
    position: fixed;
    z-index: 100000;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 0.5em;
    background-color: var(--overlay-dark);
    transition-duration: .5s;
    transition-property: all;
    transition-timing-function: ease-in;
  }

  .page-overlay .overlay-banner {
    width: 80%;
    min-width: 5em;
    max-width: 10em;
  }

  .page-overlay .overlay-title i {
    color: var(--golden);
    margin-right: 0.3em;
  }

  .page-overlay .overlay-description {
    width: 95%;
    max-width: 30em;
    text-align: center;
  }

  .page-overlay .overlay-hint {
    color: var(--grey);
    text-align: center;
  }

  .premium-introduction {
    width: 100%;
    padding: 2em 1em;
    padding-bottom: 0.5em;
  }

  .premium-introduction h1 {
    font-size: 2.5em;
    text-align: center;
    width: 100%;
    padding: 1em 5%;
    color: var(--white);
    width: 100%;
    color: var(--white);
    padding-bottom: .3em;
    line-height: 1.1em;
    padding-top: .75em;
  }

  .premium-introduction p {
    font-size: 1.05em;
    text-align: center;
    width: 100%;
    padding: 0 5%;
    color: var(--grey);
  }

  .paypal-button.paypal-button-shape-rect {
    border-radius: 3px!important;
  }

  .object-shadow {
    -webkit-box-shadow: 2px 7px 19px 8px rgba(0,0,0,0.35); 
    box-shadow: 2px 7px 19px 8px rgba(0,0,0,0.35);
  }

  .redirect-button  {
    background-color: var(--purple);
    width: 35%;
    margin-bottom: 1.45em;
  }

  @media screen and (max-width: 1070px) {
    .premium-plan-section .plan-list {
      flex-direction: column;
    }

    .premium-plan-section .plan-list .plan-card {
      max-width: 23.5em;
      width: 65%;
    }

    .redirect-button {
      width: 50%;
    }
  }

  @media screen and (max-width: 525px) {
    .redirect-button {
      width: 65%;
    }
  }

  @media screen and (max-width: 355px) {
    .premium-plan-section .plan-list .plan-card {
      min-width: 100%;
      width: 98%;
    }

    .redirect-button {
      width: 95%;
    }
  }
</style>
